<script setup lang="ts">
import { watch, computed, ref } from 'vue'
import { RouterView, useRoute } from 'vue-router'
import Location from '@/components/Location.vue'
import { useAccountStore } from './store/account'
import { storeToRefs } from 'pinia'
import capacitorInit from '@/native/capacitor'
import { init as appInfoInit } from '@/native/appinfo'
import { useMetaFilterStore } from './store/metafilter'
import { useMapsStore } from '@/store/maps'
import Sidebar from '@/components/Sidebar/Sidebar.vue'
import SidebarSection from '@/components/Sidebar/SidebarSection.vue'
import TtNotification from '@/components/TtNotification.vue'
import TtButton from '@/components/TtButton.vue'
import LocationFilter from '@/components/LocationFilter.vue'
import DummyLocationFilter from '@/components/Dummy/LocationFilter.vue'
import { SplashScreen } from '@capacitor/splash-screen'
import { useI18n } from 'vue-i18n'
import Debugger from '@/components/Debugger.vue'
import { useDebugStore } from '@/store/debug'
import LockedContent from './components/LockedContent.vue'
import SidebarSecondary from '@/components/Sidebar/SidebarSecondary/SidebarSecondary.vue'
import LogoIconDark from '@/components/Logo/LogoIconDark.vue'
import { isActiveTrialPeriod } from '@/utils/date'

// import account store
const { hasRole } = storeToRefs(useAccountStore())

const routerRoute = useRoute()

const { t } = useI18n({ useScope: 'global' })

const debug = useDebugStore()
const { filter } = storeToRefs(useMetaFilterStore())
const { route } = storeToRefs(useMapsStore())
const hasActiveFilter = computed(() => {
  if (!filter.value) return false
  return filter.value.sections.some((section) => {
    return Object.values(section.options).some((option) => option.active)
  })
})

// import { CapacitorCookies } from '@capacitor/core'
const { isLoggedIn } = storeToRefs(useAccountStore())

const { buildFilter } = useMetaFilterStore()
// App hooks etc.
capacitorInit()
appInfoInit()
watch(
  isLoggedIn,
  () => {
    if (isLoggedIn.value) {
      buildFilter()
    } else {
      // Hide splash if user is not logged in
      setTimeout(() => {
        SplashScreen.hide({
          fadeOutDuration: 1000
        })
      }, 1000)
    }
  },
  {
    immediate: true
  }
)
</script>

<template>
  <div>
    <main>
      <TtNotification>
        <Debugger v-if="debug.debug" />
        <div class="relative z-20">
          <RouterView />
        </div>
        <Suspense v-if="isLoggedIn">
          <div class="relative z-10">
            <SidebarSecondary
              v-if="
                !routerRoute.meta.isFullscreen &&
                (hasRole('siteowner') || hasRole('companyadmin'))
              "
            />
            <Sidebar
              v-if="
                !routerRoute.fullPath.includes('location') &&
                !routerRoute.fullPath.includes('route') &&
                !routerRoute.meta.isFullscreen
              "
            >
              <template #primarySidebarButton>
                <button
                  v-if="hasActiveFilter"
                  @click="filter?.resetAll()"
                  class="text-tt-green"
                >
                  {{ t('filter.restore') }}
                </button>
              </template>

              <RouterLink v-if="isActiveTrialPeriod('yearEnd2025')" to="/profile" class="mx-6 mt-8 p-6 rounded border border-tt-pale/25 text-center items-center justify-center flex flex-col">
                  <div class="flex text-lg font-semibold leading-4.5 tracking-[-0.43px]">
                      <LogoIconDark class="mr-1 block h-4 w-4" />
                      <span>TrustedTrucker gratis t.o.m. 31 december!</span>
                  </div>
                  <div class="mt-4">Hitta och förboka våra gröna parkeringar!</div>
              </RouterLink>

              <SidebarSection
                v-if="!hasRole('siteowner')"
                :noDefaultMargin="true"
                class="mt-8"
              >
                <h2 class="text-lg font-semibold leading-9 tracking-tight">
                  {{ t('route.route') }}
                </h2>
                <div class="mt-3 pb-3">
                  <TtButton
                    :as="'router-link'"
                    :to="route ? '/route' : '/route/new'"
                    :size="'large'"
                    :type="'primary'"
                  >
                    <template v-if="!route">{{
                      t('route.createNewRoute')
                    }}</template>
                    <template v-else>{{ t('route.continueOnRoute') }}</template>
                  </TtButton>
                </div>
              </SidebarSection>
              <div v-else class="mt-8"></div>
              <LockedContent logo-color="pale">
                <LocationFilter />
                <template v-slot:locked-content>
                  <DummyLocationFilter />
                </template>
              </LockedContent>
            </Sidebar>
          </div>
        </Suspense>
        <Suspense v-if="isLoggedIn && !routerRoute.meta.isFullscreen">
          <Location />
        </Suspense>
      </TtNotification>
    </main>
  </div>
</template>
